import { useId } from 'react';
import { useDispatch } from 'react-redux';
import { PrimaryButton } from 'ui/button';
import { Body2 } from 'ui/theme';
import { useHistory } from 'react-router-dom';
import { ApiError } from 'services/api/instance';
import { usePostRestoreTemplateContent } from 'api/TemplateDiffAPI';
import { addPositiveToast, addNegativeToast } from 'modules/toastContainer/utils';
import { DateFormat, TimeFormat, localizeDateTime } from 'utils/l10n';
import { ModalFooter, ModalBody, ModalHeader, Modal, MODAL_SIZES } from 'ui/modal';
import type { RestoreTemplateModalProps } from '../../types';

export const RestoreTemplateModal = ({
  templateId,
  newContent,
  onClose,
}: RestoreTemplateModalProps) => {
  const id = useId();
  const dispatch = useDispatch();
  const history = useHistory();

  const { mutate } = usePostRestoreTemplateContent({
    onSuccess: () => {
      dispatch(addPositiveToast(`Successfully restored the template content`));
      history.push(`/templates/history?templateId=${templateId}`);
      onClose();
    },
    onError: (error) => {
      const errorText =
        (error as ApiError)?.response?.data?.message || 'Failed to restore template';
      dispatch(addNegativeToast(errorText));
    },
  });

  const onSubmit = () => {
    mutate({
      templateId,
      contentId: newContent.id,
    });

    onClose();
  };

  const date = new Date(newContent.createdAt);
  const dateToDisplay: string = localizeDateTime(date, {
    dateStyle: DateFormat.Short,
    showTime: true,
    timeStyle: TimeFormat.Locale,
  });

  return (
    <Modal
      data-test="template-diff-restore-content-modal"
      id={id}
      isModalOpen
      onClose={onClose}
      size={MODAL_SIZES.SMALL}
    >
      <ModalHeader>Restore template content</ModalHeader>
      <ModalBody>
        <Body2>Do you want to restore the template to content from {dateToDisplay}?</Body2>
      </ModalBody>
      <ModalFooter onClose={onClose}>
        <PrimaryButton onClick={onSubmit}>Restore content</PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
